import { useState, forwardRef } from 'react'
import { SquareAsterisk } from 'lucide-react'
import { Popover, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import type { SecretVariable } from 'v2/shared/services/external-apps-service'
import { SecretVariablesContent } from './secret-variables.content'

interface SecretVariablesPopoverProps {
  onVariableClick: (variable: SecretVariable) => void
  className?: string
  'data-testid'?: string
  sideOffset?: number
}

interface SecretVariablesTriggerProps {
  onClick?: () => void
  className?: string
  'data-testid'?: string
}

// Separate trigger component with forwardRef for proper Popover integration
export const SecretVariablesTrigger = forwardRef<
  HTMLButtonElement,
  SecretVariablesTriggerProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(
  (
    { onClick, className = '', 'data-testid': dataTestId = 'button-open-secret-vars', ...props },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      className={`text-s-600 text-tertiary p-1 rounded-sm data-[state=open]:text-primary ${className}`}
      data-testid={dataTestId}
      aria-label="Add secret variables"
      onClick={onClick}
      {...props}
    >
      <Tooltip content="Insert secret variables">
        <SquareAsterisk className="w-4 h-4" />
      </Tooltip>
    </button>
  ),
)

SecretVariablesTrigger.displayName = 'SecretVariablesTrigger'

// Combined popover component
export const SecretVariablesPopover = ({
  onVariableClick,
  className = '',
  sideOffset = 320,
  'data-testid': dataTestId = 'button-open-secret-vars',
}: SecretVariablesPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <SecretVariablesTrigger className={className} data-testid={dataTestId} />
      </PopoverTrigger>
      <SecretVariablesContent
        onVariableClick={(variable) => {
          onVariableClick(variable)
          setIsOpen(false)
        }}
        sideOffset={sideOffset}
      />
    </Popover>
  )
}
